// src/components/AddExpense.js
import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebase';
import { addDoc, collection, getDocs, query, where } from 'firebase/firestore';
import {
  TextField,
  Button,
  FormControl,
  Typography,
  Box,
  Paper,
  Autocomplete,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  Collapse,
  Alert,
  Snackbar,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';

const RECURRENCE_OPTIONS = [
  { value: 'weekly', label: 'Settimanale' },
  { value: 'biweekly', label: 'Bisettimanale' },
  { value: 'monthly', label: 'Mensile' },
  { value: 'bimonthly', label: 'Bimestrale' },
  { value: 'quarterly', label: 'Trimestrale' },
  { value: 'yearly', label: 'Annuale' },
];

const AddExpense = ({ onClose }) => {
  const [name, setName] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [error, setError] = useState('');
  const [categories, setCategories] = useState([]);
  const [userFamily, setUserFamily] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('monthly');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchUserFamilyAndCategories = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Recupera la famiglia dell'utente
        const familiesQuery = query(
          collection(db, 'families'),
          where('members', 'array-contains', user.uid)
        );
        const familiesSnapshot = await getDocs(familiesQuery);
        let currentUserFamily = null;
        if (!familiesSnapshot.empty) {
          const familyDoc = familiesSnapshot.docs[0];
          currentUserFamily = { id: familyDoc.id, ...familyDoc.data() };
        }
        setUserFamily(currentUserFamily);

        // Recupera le categorie dalla collezione "expenses" per suggerimenti
        let categoriesQuery;
        if (currentUserFamily && currentUserFamily.id) {
          categoriesQuery = query(
            collection(db, 'expenses'),
            where('familyId', '==', currentUserFamily.id)
          );
        } else {
          categoriesQuery = query(
            collection(db, 'expenses'),
            where('userId', '==', user.uid),
            where('familyId', '==', null)
          );
        }
        const expensesSnapshot = await getDocs(categoriesQuery);
        const categoriesSet = new Set();
        expensesSnapshot.forEach((doc) => {
          const data = doc.data();
          if (data.category) {
            categoriesSet.add(data.category);
          }
        });
        setCategories(Array.from(categoriesSet));
      } catch (err) {
        console.error('Errore nel recupero dei dati:', err);
        setError('Errore nel recupero dei dati');
      }
    };

    fetchUserFamilyAndCategories();
  }, []);

  const calculateNextProcessingDate = (currentDate, type) => {
    const next = new Date(currentDate);
    switch (type) {
      case 'weekly':
        next.setDate(next.getDate() + 7);
        break;
      case 'biweekly':
        next.setDate(next.getDate() + 14);
        break;
      case 'monthly':
        next.setMonth(next.getMonth() + 1);
        break;
      case 'bimonthly':
        next.setMonth(next.getMonth() + 2);
        break;
      case 'quarterly':
        next.setMonth(next.getMonth() + 3);
        break;
      case 'yearly':
        next.setFullYear(next.getFullYear() + 1);
        break;
      default:
        return currentDate;
    }
    return next;
  };

  const handleAddExpense = async () => {
    try {
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        setError('Inserisci un importo valido.');
        return;
      }
      const currentUser = auth.currentUser;
      if (!currentUser) {
        setError('Utente non autenticato.');
        return;
      }
      // "Trimma" i campi per eliminare spazi inutili
      const trimmedName = name.trim();
      const trimmedCategory = category.trim();
      
      // Costruzione dei dati della spesa (l'importo è obbligatorio)
      const expenseData = {
        name: trimmedName,
        amount: parsedAmount,
        category: trimmedCategory,
        userId: currentUser.uid,
        familyId: userFamily?.id || null,
        date: new Date(),
        createdAt: new Date(),
      };

      // La spesa è considerata completa se entrambi i campi hanno almeno un carattere
      const isComplete = trimmedName.length > 0 && trimmedCategory.length > 0;

      if (isRecurring) {
        const recurringExpenseRef = await addDoc(
          collection(db, 'recurringExpenses'),
          {
            ...expenseData,
            recurrence: {
              type: recurrenceType,
              lastProcessed: new Date(),
              nextProcessingDate: calculateNextProcessingDate(new Date(), recurrenceType),
              isActive: true,
            },
          }
        );
        await addDoc(collection(db, 'expenses'), {
          ...expenseData,
          recurringExpenseId: recurringExpenseRef.id,
          isRecurringInstance: true,
          isComplete: true, // Le spese ricorrenti vengono considerate complete.
        });
      } else {
        await addDoc(collection(db, 'expenses'), {
          ...expenseData,
          isComplete: isComplete, // Se manca almeno un carattere in nome o categoria, è false.
        });
      }
      setSuccessMessage('Spesa aggiunta con successo!');
      setError('');
      setName('');
      setAmount('');
      setCategory('');
      setIsRecurring(false);
      setRecurrenceType('monthly');
      if (onClose) onClose();
    } catch (error) {
      console.error("Errore nell'aggiunta della spesa:", error);
      setError("Errore nell'aggiunta della spesa: " + error.message);
    }
  };

  const handleCloseSnackbar = () => {
    setSuccessMessage('');
    setError('');
  };

  return (
    <Paper style={{ padding: '20px', position: 'relative' }}>
      <Typography variant="h6" gutterBottom>
        Aggiungi una nuova spesa
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Nome Spesa (opzionale)"
          value={name}
          onChange={(e) => setName(e.target.value)}
          variant="outlined"
        />
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <TextField
          label="Importo (€)"
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          variant="outlined"
        />
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <Autocomplete
          freeSolo
          options={categories}
          value={category}
          onChange={(event, newValue) => setCategory(newValue)}
          onInputChange={(event, newInputValue) => setCategory(newInputValue)}
          renderInput={(params) => (
            <TextField {...params} label="Categoria (opzionale)" variant="outlined" />
          )}
        />
      </FormControl>
      <FormControlLabel
        control={
          <Switch
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <RepeatIcon />
            <Typography>Spesa Ricorrente</Typography>
          </Box>
        }
        sx={{ mb: 2 }}
      />
      <Collapse in={isRecurring}>
        <FormControl fullWidth sx={{ mb: 2 }}>
          <Select
            value={recurrenceType}
            onChange={(e) => setRecurrenceType(e.target.value)}
            displayEmpty
          >
            {RECURRENCE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Collapse>
      <Box display="flex" justifyContent="flex-end">
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddExpense}
          startIcon={isRecurring ? <RepeatIcon /> : null}
        >
          {isRecurring ? 'Aggiungi Spesa Ricorrente' : 'Aggiungi Spesa'}
        </Button>
      </Box>
      <Snackbar
        open={!!successMessage || !!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={successMessage ? 'success' : 'error'} sx={{ width: '100%' }}>
          {successMessage || error}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddExpense;
